import { APP_INITIALIZER, ApplicationRef, ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { Router, RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { filter, take } from 'rxjs/operators';

// Other external imports
import * as Sentry from '@sentry/capacitor';
import { Capacitor } from '@capacitor/core';
import * as SentryAngular from '@sentry/angular';
import { AngularSignaturePadModule } from '@almothafar/angular-signature-pad';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';

// Internal providers
import { AcceptInviteProvider } from './providers/accept-invite-provider/accept-invite.provider';
import { InBayProvider } from './providers/in-bay-provider/in-bay.provider';
import { ManageReservationProvider } from './providers/manage-reservations-provider/manage-reservation.provider';
import { MembershipsProvider } from './providers/memberships-provider/membership.provider';
import { StripeProvider } from './providers/stripe-provider/stripe.provider';

//Interal Services
import { AdminService } from './services/admin-service/admin.service';
import { AuthService } from './services/auth-service/auth.service';
import { BaseHttpService } from './services/base-http-service.service';
import { GuestInvitationService } from './services/guest-invitation-service/guest-invitation.service';
import { InBayService } from './services/in-bay-service/in-bay.service';
import { InVenueAdsService } from './services/in-venue/ad-service/in-venue-ads.service';
import { LeagueService } from './services/league-service/league.service';
import { MembershipService } from './services/membership-service/membership.service';
import { ReservationsService } from './services/reservations-service/reservations.service';
import { SignalrService } from './services/signalr-service/signalr.service';
import { PhoneValidationService } from './services/phone-validation-service/phone-validation.service';
import { SpotliteAuthService } from './services/spotlite-auth-service/spotlite-auth.service';
import { StripeCustomerService } from './services/stripe-service/stripe-customer/stripe-customer.service';
import { StripeService } from './services/stripe-service/stripe.service';
import { VenueService } from './services/venue-service/venue.service';
import { WalkUpPaymentService } from './services/walk-up-payment-service/walk-up-payment.service';

// Interceptors
import { AuthInterceptorService } from './interceptors/auth.interceptor';

// Chunk-load recovery
import { ChunkLoadRecoveryService } from './services/chunk-load-recovery/chunk-load-recovery.service';

//Other Internal Imports
import { environment } from '../environments/environment';

// Helper function to check if running in a proper Capacitor environment
function isCapacitorEnvironment(): boolean {
  try {
    // Check if window.webkit exists and has messageHandlers
    // This prevents errors in non-Capacitor environments like Facebook's iOS browser
    if (typeof window !== 'undefined') {
      const anyWindow = window as any;
      if (anyWindow.webkit && anyWindow.webkit.messageHandlers) {
        return true;
      }
    }
    return false;
  } catch (error) {
    // If any error occurs while checking, assume not in Capacitor environment
    return false;
  }
}

function serviceWorkerInitializer(swUpdate: SwUpdate, appRef: ApplicationRef): () => void {
  return () => {
    if (!swUpdate.isEnabled) {
      return;
    }

    const logPrefix = '[Spotlite SW]';
    const reloadApp = () => {
      if (typeof document !== 'undefined' && document.location) {
        document.location.reload();
      } else if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    };

    appRef.isStable
      .pipe(
        filter((stable) => stable),
        take(1)
      )
      .subscribe(() => {
        swUpdate.checkForUpdate().catch((err) => console.warn(`${logPrefix} update check failed`, err));
      });

    swUpdate.versionUpdates.subscribe((event) => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          console.log(`${logPrefix} downloading new version`, event.version);
          break;
        case 'VERSION_READY':
          console.log(`${logPrefix} activating new version`, event.latestVersion);
          swUpdate
            .activateUpdate()
            .then(() => reloadApp())
            .catch((err) => console.error(`${logPrefix} failed to activate new version`, err));
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error(`${logPrefix} failed to install new version`, event.error);
          break;
        case 'NO_NEW_VERSION_DETECTED':
          console.log(`${logPrefix} no updates detected`, event.version);
          break;
      }
    });

    swUpdate.unrecoverable.subscribe((event) => {
      console.error(`${logPrefix} unrecoverable state detected`, event.reason);
      reloadApp();
    });

    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log(`${logPrefix} controller changed`, new Date().toISOString());
      });
    }
  };
}

// Normalize the Sentry environment tag to one of production | stage | dev | demo.
// Keyword-based: any value containing `demo`, `stage`/`staging`, or `prod`/`production`
// (eagleeye-prod, spotlite-prod, spotlite-stage, Production, etc.) is routed to the
// canonical tag. Anything else (local, development, empty, unknown) -> dev.
function normalizeSentryEnvironment(value: string | undefined): string {
  const raw = String(value ?? '').trim().toLowerCase().replace(/^[`'"]+|[`'"]+$/g, '');
  if (raw === 'production' || raw === 'stage' || raw === 'demo' || raw === 'dev') return raw;
  if (raw.includes('demo')) return 'demo';
  if (raw.includes('staging') || raw.includes('stage')) return 'stage';
  if (raw.includes('production') || raw.includes('prod')) return 'production';
  return 'dev';
}

// Only initialize Sentry when explicitly enabled per environment
if (environment.enableSentry) {
  const sentryConfig = {
    dsn: environment.sentryDsn,
    tracesSampleRate: environment.sentryTracesSampleRate,
    release: `spotlite@${environment.appVersion}`,
    dist: environment.appDistVersion,
    environment: normalizeSentryEnvironment(environment.sentryName),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracePropagationTargets: [
      'localhost',
      'https://24.106.31.29:2289',
      'https://24.106.31.29:2286',
      'https://24.106.31.29:2287',
      'https://demo.eagleeyeapp.ai',
      'https://demo.spotlite.app',
      'https://backend-api-demo.eagleeyeapp.ai',
      'https://api-demo.eagleeyeapp.ai',
      'https://web-eagleeye-stage-fe.azurewebsites.net',
      'https://web-eagleeye-stage-fe-demo.azurewebsites.net',
      'https://web-eagleeye-prod-fe.azurewebsites.net',
      'https://web-eagleeye-proxy-stage-be.azurewebsites.net',
      'https://web-eagleeye-proxy-stage-be-demo.azurewebsites.net',
      'https://web-eagleeye-proxy-prod-be.azurewebsites.net',
      'https://eagleeyeapp.ai',
      'https://spotlite.app',

      // The eagleeyeapp.ai custom domains Spotlite ACTUALLY calls (ENG-785). Listed explicitly
      // because Sentry matches a string entry as a SUBSTRING of the request URL, and
      // 'https://eagleeyeapp.ai' is not a substring of 'https://backend-api.eagleeyeapp.ai'.
      // Keep in step with environment.*.ts baseUrl/apiUrl/proxyUrl.
      'https://backend-api.eagleeyeapp.ai',
      'https://backend-api-stage.eagleeyeapp.ai',
      'https://widget-api.eagleeyeapp.ai',
      'https://widget-api-stage.eagleeyeapp.ai',
      'https://widget-api-demo.eagleeyeapp.ai'
    ],
    replaysSessionSampleRate: environment.sentryReplaysSessionSampleRate,
    replaysOnErrorSampleRate: environment.sentryReplaysOnErrorSampleRate,
    beforeSend: (event: any, hint: any) => {
      // Transient upstream unavailability: 503 from the API/proxy while an instance recycles,
      // and Cloudflare's 530 (origin unreachable). Not actionable from the frontend, arrives in
      // bursts, and the backend reports the real cause with a stack trace. Sentry's errors quota
      // is a hard monthly cap that drops real exceptions org-wide once exhausted, so bursty
      // non-actionable failures must not consume it.
      const status = hint?.originalException?.status;
      if (status === 503 || status === 530) {
        return null;
      }

      // Drop Stripe.js "invalid origin" errors thrown by privacy browsers
      // (e.g., DuckDuckGo Mobile) that block cross-origin iframe postMessage
      // required for Stripe Connect. We surface a user-visible message in the
      // affected components instead.
      const message = hint?.originalException?.message ?? event?.message;
      const normalizedMessage = typeof message === 'string' ? message.toLowerCase() : null;
      if (normalizedMessage?.includes('invalid origin')) {
        return null;
      }
      // Safari Web Extension errors: thrown by third-party extensions when their tab
      // reference becomes stale after iOS Safari navigation. Not actionable — Spotlite
      // does not call runtime.sendMessage() itself.
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const isIosSafari =
        /iP(ad|hone|od)/.test(userAgent) &&
        /Safari/.test(userAgent) &&
        !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|Instagram|FBAN|FBAV/.test(userAgent);
      if (
        isIosSafari &&
        normalizedMessage?.includes('runtime.sendmessage') &&
        normalizedMessage.includes('tab not found')
      ) {
        return null;
      }
      // "TTRCCallbacks.onDomContentLoaded is not a function": thrown by Meta's own
      // Facebook/Instagram in-app browser, which injects a DOMContentLoaded listener
      // expecting a `TTRCCallbacks` object it never defines on pages it doesn't
      // instrument. Spotlite has no `TTRCCallbacks` symbol anywhere in its codebase —
      // the offending script isn't ours. Not actionable; drop it like the other
      // in-app-browser artifacts above.
      if (normalizedMessage?.includes('ttrccallbacks') && normalizedMessage.includes('is not a function')) {
        return null;
      }

      // ENG-899 — stamp the surface last, on whatever survived the filters above.
      return applySurfaceTag(event);
    },
    // Errors only reach beforeSend; transactions have their own hook. The filters above are
    // deliberately NOT repeated here — they drop specific error shapes and have no transaction
    // equivalent — but the surface tag has to be on both or it cannot be trusted on either.
    beforeSendTransaction: (event: any) => applySurfaceTag(event),
  };

  // Check if we're in a Capacitor environment before using Capacitor-specific initialization
  // This prevents errors in non-Capacitor WebViews (e.g., Facebook browser on iOS)
  if (isCapacitorEnvironment()) {
    // Initialize with Capacitor-specific features
    Sentry.init(sentryConfig, SentryAngular.init);
  } else {
    // Initialize with browser-only features (no Capacitor SDK)
    // Use @sentry/angular directly for non-Capacitor environments
    SentryAngular.init(sentryConfig);
  }

  applySurfaceTags();
}

/**
 * ENG-899 — say WHAT is running, on every event this app sends.
 *
 * Spotlite is one codebase serving three quite different things: an unattended wall-mounted kiosk,
 * a guest's own phone, and a staff tablet. Until now nothing distinguished them, so a Sentry issue
 * could not be read as "this is hurting guests" versus "this is a kiosk at one venue" without
 * opening events and guessing from the URL.
 *
 * Device and OS are NOT set here: Sentry's browser SDK already derives `device.family`, `os.name`
 * and `browser.name` from the user agent, and production confirms it works (iPhone, iPad, the
 * Samsung Galaxy models guests actually carry, Windows). Adding `@capacitor/device` on top would
 * buy model detail at the cost of a new plugin, a `cap sync` and a native rebuild — the same trade
 * docs/engineering/observability/pos-device-telemetry.md gates for the POS app. Not worth it while
 * the UA answer is this good.
 *
 * `Capacitor.getPlatform()` needs no new dependency (`@capacitor/core` is already here) and
 * distinguishes ios / android / web, so the Android build lands already labelled.
 *
 * `app.surface` is deliberately NOT set here — it is derived per event in `applySurfaceTag` below,
 * because a kiosk and a guest can both be served by the same running app instance.
 */
function applySurfaceTags(): void {
  try {
    // 'ios' | 'android' | 'web'. The native shells report their own platform; a guest in mobile
    // Safari reports 'web' on the same hardware, which is the distinction that matters.
    SentryAngular.setTag('app.platform', Capacitor.getPlatform());
    SentryAngular.setTag('app.native', String(Capacitor.isNativePlatform()));

  } catch {
    // Never let tagging break app startup.
  }
}

/**
 * ENG-899 — kiosk vs guest, DERIVED at send time rather than maintained.
 *
 * The obvious implementation is a pair of lifecycle hooks on the kiosk page setting and clearing a
 * tag. That was the first attempt here and it was silently broken: `KioskPage` is imported by
 * kiosk-routing.module.ts but appears in NO route — every kiosk route maps straight to its own
 * component — so its Ionic hooks never ran and every kiosk event would have been labelled 'guest'.
 * Misleading telemetry is worse than none, and this PR exists because of missing telemetry.
 *
 * Even routed correctly, enter/leave pairing is the wrong mechanism: a global mutable tag is wrong
 * for any event emitted after leave, during teardown, or after a kiosk relaunch where only the
 * startup default ran.
 *
 * An event processor has none of those failure modes. It reads the URL at the moment an event is
 * built, so it is correct by construction, needs no pairing, and cannot drift as kiosk routes are
 * added — the kiosk is everything under /kiosk, which is how app-routing.module.ts defines it.
 *
 * Wired into BOTH `beforeSend` and `beforeSendTransaction`: the first fires for error events only,
 * so with tracing sampled in production every performance transaction would otherwise arrive
 * untagged. A tag present on some events and absent on others is worse than one that is simply
 * always there — you cannot tell an untagged transaction from a guest one.
 */
export function applySurfaceTag<T extends { tags?: Record<string, unknown> }>(event: T): T {
  try {
    const path = typeof window !== 'undefined' ? window.location?.pathname ?? '' : '';
    event.tags = { ...event.tags, 'app.surface': path.startsWith('/kiosk') ? 'kiosk' : 'guest' };
  } catch {
    // A tag is never worth dropping an event for.
  }
  return event;
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    IonicModule.forRoot(),
    AngularSignaturePadModule,
    AppRoutingModule,
    SharedModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register immediately so navigation requests never race older versions.
      registrationStrategy: 'registerImmediately'
    })
  ],
  providers: [
    // Providers
    AcceptInviteProvider,
    ManageReservationProvider,
    MembershipsProvider,
    StripeProvider,
    InBayProvider,

    // Services
    AdminService,
    AuthService,
    BaseHttpService,
    GuestInvitationService,
    InBayService,
    InVenueAdsService,
    LeagueService,
    MembershipService,
    ReservationsService,
    SignalrService,
    PhoneValidationService,
    SpotliteAuthService,
    StripeCustomerService,
    StripeService,
    VenueService,
    WalkUpPaymentService,

    // Interceptors
    AuthInterceptorService,

    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: ErrorHandler,
      useValue: SentryAngular.createErrorHandler(),
    },
    {
        provide: SentryAngular.TraceService,
        deps: [Router],
    },
    {
        provide: APP_INITIALIZER,
        useFactory: () => () => {},
        deps: [SentryAngular.TraceService],
        multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: serviceWorkerInitializer,
      deps: [SwUpdate, ApplicationRef],
      multi: true,
    },
    // Recovers the app when a lazy chunk import fails (Sentry SPOTLITE-C8).
    // Must be initialised before the first navigation so no NavigationError is missed.
    {
      provide: APP_INITIALIZER,
      useFactory: (chunkRecovery: ChunkLoadRecoveryService) => () => chunkRecovery.init(),
      deps: [ChunkLoadRecoveryService],
      multi: true,
    },
  ],
  bootstrap: [
    AppComponent
  ],
})
export class AppModule {}
